import React, { useState, useRef, useEffect, useMemo, KeyboardEvent, ChangeEvent } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import CustomFieldBuilder from "components/CustomFieldBuilder";
import { Package, Settings, Filter } from 'lucide-react';
import { withIconStyle, iconLabelProps } from "components/ViewIcons";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  Row,
} from "@tanstack/react-table";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { EditIconButton } from "components/DatatablesActionIcons";
import {
  Brand,
  Category,
  CustomField,
  License,
  Office,
  Agent,
  RotationalProducts,
  serializedUnits,
  Warehouse,
  Media,
  Zone,
  Rack,
  Bin
} from "types";
import AllocationToBins from "components/AllocationToBin";
import { UIInputFF, UIReactSelectFF } from "components/Adapter";
import Select, { SingleValue, components } from "react-select";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { uploadFilesAndGetIds, hardDeletePresignedFiles, } from "components/Imageshelper";
import { getAuthCookie } from "../../utils/authCookies";

// NOTE: UI-only redesign for Product Details + Images tabs.
// All logic, APIs, state names, and payload shapes remain identical.

type Attribute = {
  id: string;
  name: string;
  values: string[];
  description: string;
  status: string;
};

type SerializedUnit = {
  serial_id: string;
  serial_number: string;
  condition: string;
  status: string;
  order_number: string;
  warehouse: string;
  zone: string;
  rack: string;
  bin: string;
  warehouse_id: string;
  zone_id: string;
  rack_id: string;
  bin_id: string;
};

type Combo = {
  warehouse_id: string;
  warehouse: string;
  zone_id: string;
  zone: string;
  rack_id: string;
  rack: string;
  bin_id: string;
  bin: string;
};

type RowEditState = {
  warehouse_id?: string;
  bin_id?: string;
  zone_id?: string;
  rack_id?: string;
  status?: string;
};

/** Hide arrow when disabled */
const DropdownIndicator = (props: any) => {
  if (props.selectProps.isDisabled) return null;
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
};

/** Custom Control component with Filter icon */
const ControlWithFilter = (props: any) => {
  return (
    <components.Control {...props}>
      <Filter size={16} style={{ color: '#6c757d', marginLeft: '12px', marginRight: '8px', flexShrink: 0 }} />
      {props.children}
    </components.Control>
  );
};

const customSelectStyles = {
  /* hide all indicators when disabled */
  indicatorsContainer: (provided: any, state: any) => ({
    ...provided,
    display: state.isDisabled ? "none" : "flex",
  }),
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    display: state.isDisabled ? "none" : "flex",
  }),
  indicatorSeparator: () => ({ display: "none" }),

  /* keep bg and cursor right; do NOT fade */
  control: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isDisabled ? "#f2f2f2" : provided.backgroundColor,
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
  }),

  /* ——— make text BLACK when disabled ——— */
  singleValue: (provided: any, state: any) => ({
    ...provided,
    color: state.isDisabled ? "#000" : provided.color,
  }),
  valueContainer: (provided: any, state: any) => ({
    ...provided,
    color: state.isDisabled ? "#000" : provided.color,
  }),
  input: (provided: any, state: any) => ({
    ...provided,
    color: state.isDisabled ? "#000" : provided.color,
    WebkitTextFillColor: state.isDisabled ? "#000" : undefined, // Safari
  }),
  placeholder: (provided: any, state: any) => ({
    ...provided,
    /* if field has no value, keep placeholder readable too */
    color: state.isDisabled ? "#000" : provided.color,
  }),

  /* if you ever use isMulti, keep chips readable */
  multiValue: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor,
  }),
  multiValueLabel: (p: any, s: any) => ({
    ...p,
    color: s.isDisabled ? "#000" : p.color,
  }),
  multiValueRemove: (p: any, s: any) => ({
    ...p,
    display: s.isDisabled ? "none" : p.display,
  }),

  /* menu z-index to appear above table */
  menu: (provided: any) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 9999,
  }),
};

// Style for table dropdowns (Warehouse and Status columns) - matches Filter by Status dropdown
const tableSelectStyles = {
  ...customSelectStyles,
  control: (provided: any, state: any) => ({
    ...provided,
    ...customSelectStyles.control(provided, state),
    backgroundColor: state.isDisabled ? "#f2f2f2" : "#fff",
    border: state.isFocused ? "0.5px solid #9ca3af" : "0.5px solid #e5e7eb",
    borderRadius: "8px",
    minHeight: "38px",
    boxShadow: state.isFocused ? "0 0 0 1px #9ca3af" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#9ca3af" : "#d1d5db",
      backgroundColor: "#f9fafb",
    },
  }),
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    ...customSelectStyles.dropdownIndicator(provided, state),
    color: state.isFocused ? "#9ca3af" : "#6b7280",
    "&:hover": {
      color: "#9ca3af",
    },
  }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  }),
};

const SignageEditRotationalProduct: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const [rotationalProducts, setRotationProducts] = useState<RotationalProducts>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [preview, setPreview] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const [brokerageFirms, setBrokerageFirms] = useState<License[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [racks, setRacks] = useState<Rack[]>([]);
  const [bins, setBins] = useState<Bin[]>([]);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [serializeQuantity, setSerializeQuantity] = useState<string>("");
  const [quantityError, setQuantityError] = useState("");
  const [customFields, setCustomFields] = useState<CustomField[] | undefined>(undefined);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
  const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);

  const [options, setOptions] = useState<{ attribute: string; value: string }[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const [selectedBrokerageFirm, setSelectedBrokerageFirm] = useState<{
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null>(null);
  const [selectedOffice, setSelectedOffice] = useState<{
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<{
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null>(null);
  const [selecteWarehouse, setSelectedWarehouse] = useState<{ value: string; label: string } | null>(null);

  const [agentLinkedProducts, setAgentagentLinkedProducts] = useState<
    { agentId: string; warehouseId: string; allocatedQty: string; availableQty: number }[]
  >([]);
  const [officeLinkedProducts, setOfficeLinkedProducts] = useState<
    { officeId: string; warehouseId: string; allocatedQty: string; availableQty: number }[]
  >([]);

  // EDITABLE warehouse list used by the UI (label + available)
  const [editableWarehouses, setEditableWarehouses] = useState<
    { warehouseId: string; label: string; available: number }[]
  >([]);

  const [warehouseTotals, setWarehouseTotals] = useState({ total: 0, serialized: 0, available: 0 });

  // Search and filter state for serialization tab
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<{ value: string; label: string } | null>(null);
  const [selectedWarehouseIdForSerialization, setSelectedWarehouseIdForSerialization] = useState<string>("");
  
  // Serialized units datatable state
  const [serializedUnits, setSerializedUnits] = useState<SerializedUnit[]>([]);
  const [serializedUnitsLoading, setSerializedUnitsLoading] = useState(false);
  const [serializedUnitsPagination, setSerializedUnitsPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [serializedUnitsRowCount, setSerializedUnitsRowCount] = useState(0);
  const [serializedUnitsGlobalFilter, setSerializedUnitsGlobalFilter] = useState("");
  const [rowEdits, setRowEdits] = useState<Record<string, RowEditState>>({});
  const [editingRowId, setEditingRowId] = useState<string | null>(null);

  // Pagination (kept same logic: 5 per page)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // NEW: Controlled tabs like other redesigned pages
  const [activeTab, setActiveTab] = useState<"details" | "images" | "serialization" | "allocation">("details");

  useEffect(() => {
    if (rotationalProducts?.rotational_product?.images) {
      const normalized = rotationalProducts.rotational_product.images.map((img: any) => ({
        url: (img?.url || "").replace(/\\/g, "/"),
        alt_text: img?.alt_text,
      }));
      setUploadedFileMeta(normalized);
    }
  }, [rotationalProducts]);

  useEffect(() => {
    const fetchRotationProducts = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products_without_serialization/${productId}`,
          { method: "GET", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
        const data = await response.json();
        setRotationProducts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetchRotationProducts();
  }, [token]);

  useEffect(() => {
    const fetchBrokerageFirms = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/brokerage-firms`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await response.json();
        setBrokerageFirms(data);
        if (!response.ok) showError(data.error || "Failed to fetch firms");
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetchBrokerageFirms();
  }, [token]);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/brands?permissions=false`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await response.json();
        setBrands(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, [token]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/categories?permissions=false`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, [token]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/warehouses?permissions=false`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await response.json();
        setWarehouses(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetchWarehouses();
  }, [token]);

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/product-attributes`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await response.json();
        setAttributes(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetchAttributes();
  }, [token]);

  // Fetch zones, racks, bins
  useEffect(() => {
    const fetchAllZones = async (): Promise<Zone[]> => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/zones`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to fetch zones");
        return data;
      } catch (err) {
        console.error("Zones fetch error", err);
        return [];
      }
    };

    const fetchAllRacks = async (): Promise<Rack[]> => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/racks`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to fetch racks");
        return data;
      } catch (err) {
        console.error("Racks fetch error", err);
        return [];
      }
    };

    const fetchAllBins = async (): Promise<Bin[]> => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/bins`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to fetch bins");
        return data;
      } catch (err) {
        console.error("Bins fetch error", err);
        return [];
      }
    };

    (async () => {
      const [z, r, b] = await Promise.all([fetchAllZones(), fetchAllRacks(), fetchAllBins()]);
      setZones(z);
      setRacks(r);
      setBins(b);
    })();
  }, [token]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);


  async function uploadProductImagesToCloudDrive(): Promise<string[]> {
    const tok = (token || "").trim() || null;
    // use the files you already collected in previewFiles
    const files = (previewFiles || []).map(p => p.file);
    if (!files.length) return [];

    const ids = await uploadFilesAndGetIds(files, tok);
    setPreviewFiles([]);           // clear after successful upload
    return ids;                    // send back to onSubmit
  }
  // If you have a single product object in edit screen:
  const dataForMap = React.useMemo(() => (rotationalProducts ? rotationalProducts : []), [rotationalProducts]);
  console.log("dataForMap=12", dataForMap);
  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: rotationalProducts?.rotational_product?.name || "",
      sku: rotationalProducts?.rotational_product?.sku || "",
      description: rotationalProducts?.rotational_product?.description || "",
      brand: rotationalProducts?.rotational_product?.brand || "",
      category: rotationalProducts?.rotational_product?.category || "",
      item_condition: rotationalProducts?.rotational_product?.item_condition || "",
      condition_note: "",
      purchase_price: String(rotationalProducts?.rotational_product?.purchase_price || ""),
      retail_price: String(rotationalProducts?.rotational_product?.retail_price || ""),
      length: String(rotationalProducts?.rotational_product?.package_dimensions?.length || ""),
      height: String(rotationalProducts?.rotational_product?.package_dimensions?.height || ""),
      width: String(rotationalProducts?.rotational_product?.package_dimensions?.width || ""),
      weight: String(rotationalProducts?.rotational_product?.package_dimensions?.weight || ""),
      threshold_qty: String(rotationalProducts?.rotational_product?.threshold_qty || ""),
      attribute: "",
      parameter: "",
      notes: "",
      image: null,
      statuss: rotationalProducts?.rotational_product?.status || "",
      warehouse: "",
      allocated_qty: 0,
      available_qty: 0,
      warehouseId: "",
      linked_agent: "",
      select_user: "",
      select_warehouse_id: "",
      serialize_quantity: "",
      serial_prefix: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .min(2, "Name must be at least 2 characters long")
        .max(250, "Name cannot be longer than 50 characters")
        .required("Name is required"),
      sku: Yup.string().required("SKU is required"),
      retail_price: Yup.number().required("Retail price is required"),
    }),
    onSubmit: async (values) => {
      setFormSubmitting(true);
      try {
        const newImageIds = await uploadProductImagesToCloudDrive();

        // 2) Convert *existing* (kept) images from state as-is
        const kept: Media[] = (uploadedFileMeta ?? []).map((m: any) => ({
          url: m?.url,
          alt_text: m?.alt_text || "",
          default: !!m?.default,
        }));

        // 3) Convert *new* uploads into Media[]
        const newMedia: Media[] = newImageIds.map((id, idx) => ({
          url: id,
          alt_text: (previewFiles?.[idx]?.file?.name as string) || "",
          default: false,
        }));

        // 4) Merge and ensure exactly one default (if none, make the first one default)
        let images: Media[] = [...kept, ...newMedia];
        if (!images.some((m) => m.default) && images.length > 0) {
          images = images.map((m, i) => ({ ...m, default: i === 0 }));
        }

        try {
          if (pendingDeleteIds.length > 0) {
            await hardDeletePresignedFiles(pendingDeleteIds, token || null);
            setPendingDeleteIds([]); // clear
          }
        } catch (delErr: any) {
          console.error("Image hard delete (batch) failed:", delErr);
          // Don't block success — just inform
          showError(delErr?.message || "Some removed images could not be deleted.");
        }
        // Detect and update changed warehouses first (logic unchanged)
        const changedWarehouses = editableWarehouses.filter((w) => {
          const original = rotationalProducts?.warehouse_details?.find(
            (ow) => ow.warehouse._id === w.warehouseId
          );
          return original && original.available !== w.available;
        });
        let lastWarehouseUpdateResponse: any = null;
        if (changedWarehouses.length > 0) {
          for (const w of changedWarehouses) {
            const whDetail = rotationalProducts?.warehouse_details?.find(
              (ow) => ow.warehouse._id === w.warehouseId
            );
            const location: any = whDetail?.location || {};
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products/${productId}/warehouse/${w.warehouseId}`,
              {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({
                  available: w.available,
                  location: {
                    warehouse_id: w.warehouseId,
                    warehouse_name: w.label,
                    zone_id: location.zone_id || "",
                    zone_name: location.zone || "",
                    rack_id: location.rack_id || "",
                    rack_name: location.rack || "",
                    bin_id: location.bin_id || "",
                    bin_name: location.bin || "",
                  },
                }),
              }
            );
            const data: any = await response.json();
            if (!response.ok || data?.error) {
              const message = data?.error || data?.message || "Failed to save data";
              showError(message);
              throw new Error(message);
            }
            showSuccess("Rotational Product updated successfully!");
            navigate(`${baseRoute}/rotationalProducts`);
            lastWarehouseUpdateResponse = data;
          }
        }

        const updatedWarehouseDetails =
          lastWarehouseUpdateResponse?.warehouse_details || rotationalProducts?.warehouse_details || [];
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products/${productId}`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({
              rotational_product: {
                name: values.name,
                sku: values.sku,
                brand: values.brand,
                category: values.category,
                description: values.description,
                item_condition: values.item_condition,
                purchase_price: Number(values.purchase_price),
                retail_price: Number(values.retail_price),
                package_dimensions: {
                  length: Number(values.length) || 0,
                  width: Number(values.width) || 0,
                  height: Number(values.height) || 0,
                  weight: Number(values.weight) || 0,
                  weight_type: "kg",
                },
                variants: [],
                is_rotational: true,
                linked_mode: "none",
                locked_status: "none",
                product_location_status: "none",
                current_status: "none",
                images: images || "",
                status: values.statuss,
              },
              warehouse_details: updatedWarehouseDetails,
              total_quantity: updatedWarehouseDetails.reduce(
                (acc: any, item: any) => acc + Number(item.available || 0),
                0
              ),
            }),
          }
        );
        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to save data";
          showError(message);
          throw new Error(message);
        }
        showSuccess("Product Updated successfully!");
        navigate(`${baseRoute}/rotationalProducts`);
      } catch (error) {
        console.error("Fetch Error:", error);
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  const addAgentLinkedProduct = () =>
    setAgentagentLinkedProducts([
      ...agentLinkedProducts,
      { agentId: "", warehouseId: "", allocatedQty: "", availableQty: 0 },
    ]);
  const removeAgentLinkedProduct = (index: number) =>
    setAgentagentLinkedProducts((prev) => prev.filter((_, i) => i !== index));
  const handleAgentChange = (index: number, selected: any) =>
    setAgentagentLinkedProducts((prev) => {
      const u = [...prev];
      u[index].agentId = selected.value;
      return u;
    });
  const handleAgentWarehouseChange = (index: number, selected: any) =>
    setAgentagentLinkedProducts((prev) => {
      const u = [...prev];
      u[index].warehouseId = selected.value;
      return u;
    });
  const handleAgentAvailableChange = (index: number, value: number) =>
    setAgentagentLinkedProducts((prev) => {
      const u = [...prev];
      u[index].availableQty = value;
      return u;
    });

  const addOfficeLinkedProduct = () =>
    setOfficeLinkedProducts([
      ...officeLinkedProducts,
      { officeId: "", warehouseId: "", allocatedQty: "", availableQty: 0 },
    ]);
  const removeOfficeLinkedProduct = (index: number) =>
    setOfficeLinkedProducts((prev) => prev.filter((_, i) => i !== index));
  const handleOfficeChange = (index: number, selected: any) =>
    setOfficeLinkedProducts((prev) => {
      const u = [...prev];
      u[index].officeId = selected.value;
      return u;
    });
  const handleOfficeWarehouseChange = (index: number, selected: any) =>
    setOfficeLinkedProducts((prev) => {
      const u = [...prev];
      u[index].warehouseId = selected.value;
      return u;
    });
  const handleOfficeAvailableChange = (index: number, value: number) =>
    setOfficeLinkedProducts((prev) => {
      const u = [...prev];
      u[index].availableQty = value;
      return u;
    });

  const warehouseOptions = [
    { value: "", label: "-- Select Warehouse --" },
    ...((warehouses || []).map((warehouse) => ({
      value: warehouse?._id,
      label: warehouse?.label,
    }))),
  ];


  const allocateQuantity = async (inventorySource: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products/${productId}/assign`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: "", quantity: "", inventory_source: inventorySource }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        console.error("API Error:", errorData);
        return;
      }
      const result = await response.json();
      console.log("Allocation Success:", result);
    } catch (error) {
      console.error("Fetch Error:", error);
    }
  };

  useEffect(() => {
    if (rotationalProducts?.warehouse_details) {
      const mapped = rotationalProducts.warehouse_details?.map((w) => ({
        warehouseId: w.warehouse._id,
        label: w.warehouse.label,
        available: w.available || 0,
      }));
      setEditableWarehouses(mapped);
    }
  }, [rotationalProducts]);

  const handleGenerateSerialization = async (quantity: number) => {
    const { select_warehouse_id, serial_prefix } = formik.values as any;
    if (!select_warehouse_id || !quantity) {
      showError("Please select a warehouse and enter a quantity.");
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products/${productId}/generate-serialization`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            warehouse_id: select_warehouse_id,
            quantity: quantity,
            serial_prefix: serial_prefix || "",
          }),
        }
      );
      const result = await response.json();
      navigate(0);
      if (!response.ok) {
        showError(result.message || "Serialization failed.");
        return;
      }
      showSuccess("Serialization generated successfully.");
    } catch (error) {
      console.error("Error generating serialization:", error);
      showError("Unexpected error occurred.");
    }
  };

  const fetchWarehouseSerializationData = async (warehouseId: string) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products/${productId}/warehouses/${warehouseId}/serial-count`,
        { method: "GET", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      const data = await res.json();
      if (!res.ok) {
        showError(data.message || "Failed to fetch warehouse serialization data");
        return;
      }
      // Map API response to warehouse totals
      setWarehouseTotals({
        total: data.available || 0,  // Total Quantity
        serialized: data.serialized_unit_count || 0,  // Serialized Quantity
        available: data.unallocated_serial_count || 0,  // Available Quantity (for allocation)
      });
    } catch (err) {
      console.error("Serialization fetch error", err);
      // showError("Error getting warehouse serialization data");
    }
  };

  // Fetch serialized units from datatable API
  const fetchSerializedUnits = async () => {
    if (!productId || !userId) return;
    
    setSerializedUnitsLoading(true);
    try {
      const statusValue = statusFilter?.value === "all" ? "" : statusFilter?.value || "";
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational/${productId}/serialized-units/datatable`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page: serializedUnitsPagination.pageIndex + 1,
            limit: serializedUnitsPagination.pageSize,
            search: serializedUnitsGlobalFilter,
            status: statusValue,
            condition: "",
            warehouse_id: selectedWarehouseIdForSerialization,
            sort: {
              field: "order_number",
              order: "asc",
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch serialized units");
      }

      const result = await response.json();
      setSerializedUnitsRowCount(result.total || 0);
      setSerializedUnits(result?.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setSerializedUnitsLoading(false);
    }
  };

  // Fetch serialized units when serialization tab is active
  useEffect(() => {
    if (activeTab === "serialization" && productId && userId) {
      fetchSerializedUnits();
    }
  }, [activeTab, serializedUnitsPagination, serializedUnitsGlobalFilter, statusFilter, selectedWarehouseIdForSerialization, productId, userId, token]);

  function fuzzyTextFilter<TData>(row: Row<TData>, columnId: string, filterValue: string) {
    const raw = row.getValue(columnId);
    const value = String(raw || "").toLowerCase();
    return value.includes(filterValue.toLowerCase());
  }

  // Build combinations for warehouse > zone > rack > bin (like AllocationToBin)
  const combinations: Combo[] = useMemo(() => {
    const whName: Record<string, string> = {};
    warehouses?.forEach((w: any) => (whName[w._id] = w.label));

    const out: Combo[] = [];
    racks?.forEach((rk: any) => {
      const z = zones.find((zz: any) => zz._id === rk.zone_id);
      bins.forEach((bn: any) => {
        if (bn.rack_id === rk._id) {
          out.push({
            warehouse_id: z?.warehouse_id || rk.warehouse_id || "",
            warehouse: whName[z?.warehouse_id || rk.warehouse_id || ""] || "",
            zone_id: z?._id || "",
            zone: z?.label || "",
            rack_id: rk?._id || "",
            rack: rk?.label || rk?.rack || rk?.name || "",
            bin_id: bn?._id || "",
            bin: bn?.label || bn?.bin || bn?.name || "",
          });
        }
      });
    });
    return out;
  }, [warehouses, zones, racks, bins]);

  // Get bin location string for a serialized unit
  const getBinLocationString = (unit: SerializedUnit): string => {
    if (!unit.warehouse_id) return "---";
    const combo = combinations.find(
      (c) =>
        c.warehouse_id === unit.warehouse_id &&
        c.zone_id === unit.zone_id &&
        c.rack_id === unit.rack_id &&
        c.bin_id === unit.bin_id
    );
    if (combo) {
      return `${combo.warehouse} > ${combo.zone} > ${combo.rack} > ${combo.bin}`;
    }
    // Fallback to individual values if combo not found
    const parts = [unit.warehouse, unit.zone, unit.rack, unit.bin].filter(Boolean);
    return parts.length > 0 ? parts.join(" > ") : "---";
  };

  // Build bin options for dropdown (warehouse > zone > rack > bin format)
  const binOptions = useMemo(() => {
    return combinations.map((combo) => ({
      value: combo.bin_id,
      label: `${combo.warehouse} > ${combo.zone} > ${combo.rack} > ${combo.bin}`,
      combo: combo,
    }));
  }, [combinations]);

  const columnHelper = createColumnHelper<SerializedUnit>();

  const serializedUnitsColumns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No">S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No">
            {serializedUnitsPagination.pageIndex * serializedUnitsPagination.pageSize + info.row.index + 1}
          </div>
        ),
        size: 80,
      }),
      columnHelper.display({
        id: "warehouse",
        header: () => <div className="th-text">Warehouse</div>,
        cell: (info) => {
          const unit = info.row.original;
          const isEditing = editingRowId === unit.serial_id;
          const currentBinId = rowEdits[unit.serial_id]?.bin_id || unit.bin_id;
          const currentCombo = combinations.find(
            (c) =>
              c.warehouse_id === (rowEdits[unit.serial_id]?.warehouse_id || unit.warehouse_id) &&
              c.bin_id === currentBinId
          );
          const displayValue = currentCombo
            ? `${currentCombo.warehouse} > ${currentCombo.zone} > ${currentCombo.rack} > ${currentCombo.bin}`
            : getBinLocationString(unit);

          // Filter bin options by warehouse if warehouse is selected
          const filteredBinOptions = rowEdits[unit.serial_id]?.warehouse_id
            ? binOptions.filter((opt) => opt.combo.warehouse_id === rowEdits[unit.serial_id]?.warehouse_id)
            : binOptions;

          if (isEditing) {
            return (
              <div className="text-sm">
                <select
                  className="form-control text-center br-8 select-with-arrow"
                  value={currentBinId || ""}
                  onChange={(e) => {
                    const selectedBinId = e.target.value;
                    const selectedOption = binOptions.find((opt) => opt.value === selectedBinId);
                    if (selectedOption?.combo) {
                      setRowEdits((prev) => ({
                        ...prev,
                        [unit.serial_id]: {
                          ...prev[unit.serial_id],
                          warehouse_id: selectedOption.combo.warehouse_id,
                          bin_id: selectedOption.combo.bin_id,
                          zone_id: selectedOption.combo.zone_id,
                          rack_id: selectedOption.combo.rack_id,
                        },
                      }));
                    }
                  }}
                >
                  {/* <option value="">-- Select Location --</option> */}
                  {filteredBinOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          }

          return <div className="text-sm">{displayValue}</div>;
        },
        size: 120,
      }),
      columnHelper.accessor((row: SerializedUnit) => row?.serial_number || "-", {
        id: "serial_number",
        header: () => <div className="th-text">Serial Number</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        size: 100,
      }),
      columnHelper.accessor((row: SerializedUnit) => row?.order_number || "-", {
        id: "order_number",
        header: () => <div className="th-text">Order Number</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        size: 150,
      }),
      columnHelper.accessor((row: SerializedUnit) => {
        const unit = row;
        return rowEdits[unit.serial_id]?.status || unit.status || "-";
      }, {
        id: "status",
        header: () => <div className="th-text">Status</div>,
        cell: (info) => {
          const unit = info.row.original;
          const isEditing = editingRowId === unit.serial_id;
          const status = rowEdits[unit.serial_id]?.status || info.getValue();
          
          // Status color configuration
          const statusConfig: { [key: string]: { color: string; bgColor: string; borderColor: string } } = {
            Damaged: {
              color: "#9f0712",
              bgColor: "#ffe2e2",
              borderColor: "#ffc9c9",
            },
            Lost: {
              color: "#1e2939",
              bgColor: "#f6f3f4",
              borderColor: "#ebe6e7",
            },
            "Repair Required": {
              color: "#894b00",
              bgColor: "#fef9c2",
              borderColor: "#fff085",
            },
            "Under maintenance": {
              color: "#894b00",
              bgColor: "#fef9c2",
              borderColor: "#fff085",
            },
            Available: {
              color: "#016630",
              bgColor: "#dcfce7",
              borderColor: "#b9f8cf",
            },
          };
          const currentStatusConfig = statusConfig[status] || statusConfig["Lost"];
          
          // Map status value to display label
          const getStatusLabel = (statusValue: string): string => {
            if (statusValue === "Under maintenance") return "Repair Required";
            return statusValue;
          };

          if (isEditing) {
            return (
              <div className="text-sm">
                <select
                  className="form-control text-center br-8 select-with-arrow"
                  style={{
                    backgroundColor: currentStatusConfig.bgColor,
                    borderColor: currentStatusConfig.borderColor,
                    color: currentStatusConfig.color,
                    fontWeight: "500",
                  }}
                  value={status}
                  onChange={(e) => {
                    setRowEdits((prev) => ({
                      ...prev,
                      [unit.serial_id]: {
                        ...prev[unit.serial_id],
                        status: e.target.value,
                      },
                    }));
                  }}
                >
                  <option value="Damaged">Damaged</option>
                  <option value="Lost">Lost</option>
                  <option value="Under maintenance">Repair Required</option>
                </select>
              </div>
            );
          }

          return (
            <div className="text-sm">
              <div
                className="form-control text-center br-8"
                style={{
                  backgroundColor: currentStatusConfig.bgColor,
                  borderColor: currentStatusConfig.borderColor,
                  color: currentStatusConfig.color,
                  fontWeight: "500",
                  cursor: "default",
                }}
              >
                {getStatusLabel(status)}
              </div>
            </div>
          );
        },
        size: 180,
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => {
          const unit = info.row.original;
          const isEditing = editingRowId === unit.serial_id;
          
          const handleEdit = () => {
            setEditingRowId(unit.serial_id);
          };

          const handleCancel = () => {
            setEditingRowId(null);
            // Clear edits for this row
            setRowEdits((prev) => {
              const newEdits = { ...prev };
              delete newEdits[unit.serial_id];
              return newEdits;
            });
          };

          const handleSave = async () => {
            const edits = rowEdits[unit.serial_id];
            if (!edits || !productId || !userId || !token) return;

            try {
              // Find the combo to get names
              const combo = combinations.find(
                (c) =>
                  c.warehouse_id === (edits.warehouse_id || unit.warehouse_id) &&
                  c.zone_id === (edits.zone_id || unit.zone_id) &&
                  c.rack_id === (edits.rack_id || unit.rack_id) &&
                  c.bin_id === (edits.bin_id || unit.bin_id)
              );

              // Build request body
              const requestBody: any = {};

              // Add status if changed
              if (edits.status) {
                requestBody.status = edits.status;
              }

              // Add location if warehouse/bin changed
              if (edits.warehouse_id || edits.bin_id || edits.zone_id || edits.rack_id) {
                requestBody.location = {
                  warehouse_id: edits.warehouse_id || unit.warehouse_id || "",
                  warehouse_name: combo?.warehouse || unit.warehouse || "",
                  zone_id: edits.zone_id || unit.zone_id || "",
                  zone_name: combo?.zone || unit.zone || "",
                  rack_id: edits.rack_id || unit.rack_id || "",
                  rack_name: combo?.rack || unit.rack || "",
                  bin_id: edits.bin_id || unit.bin_id || "",
                  bin_name: combo?.bin || unit.bin || "",
                };
              }

              const response = await fetch(
                `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational/${productId}/serialized-units/${unit.serial_id}`,
                {
                  method: "PATCH",
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify(requestBody),
                }
              );

              const result = await response.json();

              if (!response.ok || result?.error) {
                showError(result?.error || result?.message || "Failed to update serialized unit");
                return;
              }

              showSuccess("Serialized unit updated successfully");
              
              // Clear the edit state
              setEditingRowId(null);
              setRowEdits((prev) => {
                const newEdits = { ...prev };
                delete newEdits[unit.serial_id];
                return newEdits;
              });

              // Refresh the datatable
              fetchSerializedUnits();
            } catch (error) {
              console.error("Error updating serialized unit:", error);
              showError("An error occurred while updating the serialized unit");
            }
          };
          
          return (
            <div className="text-sm d-flex gap-2 justify-content-center">
              {isEditing ? (
                <>
                  <button
                    className="btn btn-sm border-0 p-0 me-2"
                    onClick={handleSave}
                    title="Save"
                  >
                    <i className="ri-check-line fs-18" style={{ color: "green" }}></i>
                  </button>
                  <button
                    className="btn btn-sm border-0 p-0"
                    onClick={handleCancel}
                    title="Cancel"
                  >
                    <i className="ri-close-line fs-18" style={{ color: "red" }}></i>
                  </button>
                </>
              ) : (
                <EditIconButton
                  onClick={handleEdit}
                  title="Edit"
                />
              )}
            </div>
          );
        },
        size: 100,
      }),
    ];
  }, [serializedUnitsPagination, warehouseOptions, combinations, binOptions, rowEdits, editingRowId]);

  const serializedUnitsTable = useReactTable({
    data: serializedUnits,
    columns: serializedUnitsColumns,
    pageCount: Math.ceil(serializedUnitsRowCount / serializedUnitsPagination.pageSize),
    state: {
      pagination: serializedUnitsPagination,
      globalFilter: serializedUnitsGlobalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setSerializedUnitsPagination,
    onGlobalFilterChange: setSerializedUnitsGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
  });

  // ===== Helpers for the Warehouse Quantities UI (UI-only) =====
  const handleWarehouseFieldChange = (warehouseId: string, value: number) => {
    const safe = Math.max(0, Number(value) || 0);
    setEditableWarehouses((prev) =>
      prev.map((it) => (it.warehouseId === warehouseId ? { ...it, available: safe } : it))
    );
  };

  // build data for the redesigned cards section
  const warehouseDetails = useMemo(
    () =>
      (editableWarehouses || []).map((w) => ({
        warehouseId: w.warehouseId,
        label: w.label,
        availableQty: w.available ?? 0,
      })),
    [editableWarehouses]
  );
  const totalPagesWQ = Math.max(1, Math.ceil(warehouseDetails.length / itemsPerPage));
  const paginatedData = useMemo(
    () => warehouseDetails.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [warehouseDetails, currentPage]
  );
  const handlePageChange = (p: number) => setCurrentPage(p);

  const warehouseMap = useMemo(() => {
    const map: Record<string, string> = {};
    warehouses?.forEach((w) => {
      map[w._id] = w.label;
    });
    return map;
  }, [warehouses]);

  const removeUploadedImages = (ids: string[]) => {
    const clean = (ids || []).map(String).filter(Boolean);
    if (!clean.length) return;

    // mark for delete on SAVE
    setPendingDeleteIds(prev => Array.from(new Set([...(prev || []), ...clean])));

    // remove from current UI list
    setUploadedFileMeta(prev => {
      const next = (prev || []).filter((m: any) => !clean.includes(String(m?.url)));
      // ensure exactly one default if any images remain
      if (next.length && !next.some((m: any) => m?.default)) {
        next[0] = { ...next[0], default: true };
      }
      return next;
    });
  };

  /** Convenience: remove a single existing image by its file id (stored in `url`). */
  const removeUploadedImage = (id: string) => removeUploadedImages([id]);

  /** Remove a local (not uploaded yet) preview by its index. */
  const removePreviewImage = (index: number) => {
    setPreviewFiles(prev => {
      const arr = [...prev];
      const target = arr[index];
      if (target?.url) URL.revokeObjectURL(target.url);
      arr.splice(index, 1);
      return arr;
    });
  };

  return (
    <div className="edit-rotational-product container-xxl py-3">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0 fw-bold">Edit Asset Product</h4>
        {/* <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
            <i className="ri-arrow-left-line me-1" />
            Back
          </button>
          <Link to={`${baseRoute}/viewRotationalProduct/${productId}`} className="btn btn-light btn-sm">
            <i className="ri-eye-line me-1" />
            View
          </Link>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => formik.handleSubmit()}
            disabled={formSubmitting}
          >
            {formSubmitting ? "Saving…" : "Save"}
          </button>
        </div> */}
      </div>

      {/* Tabs */}
      <div className="rc-tabs mb-3">
        <button
          type="button"
          className={`rc-tab ${activeTab === "details" ? "rc-tab-active" : ""}`}
          onClick={() => setActiveTab("details")}
        >
          <i className="ri-box-3-line me-2" />
          Product Details
        </button>
        <button
          type="button"
          className={`rc-tab ${activeTab === "images" ? "rc-tab-active" : ""}`}
          onClick={() => setActiveTab("images")}
        >
          <i className="ri-image-2-line me-2" />
          Images
        </button>
        <button
          type="button"
          className={`rc-tab ${activeTab === "serialization" ? "rc-tab-active" : ""}`}
          onClick={() => setActiveTab("serialization")}
        >
          <i className="ri-barcode-line me-2" />
          Serialization
        </button>
        <button
          type="button"
          className={`rc-tab ${activeTab === "allocation" ? "rc-tab-active" : ""}`}
          onClick={() => setActiveTab("allocation")}
        >
          <i className="ri-git-branch-line me-2" />
          Allocation
        </button>
      </div>

      {/* ================= PRODUCT DETAILS (UI refreshed only) ================= */}
      {activeTab === "details" && (
        <form onSubmit={formik.handleSubmit} className="box-shadow-datatables shadow-border-1 p-5">
          <div className="row g-4">
            {/* LEFT */}
            <div className="col-lg-6">
              <div className="mb-3 fw-semibold text-uppercase small text-secondary">
                <div {...iconLabelProps(16)}>
                  <Package {...withIconStyle("#4a5565", 19)} />

                  <span className="header-color-product"> Product Information</span>
                </div>
              </div>

              <div className="mb-3">
                <UIInputFF
                  formik={formik as any}
                  name="name"
                  label="Product Name"
                  required
                  placeholder="Enter product name"
                />
              </div>

              <div className="mb-3 col-lg-8">
                <UIReactSelectFF
                  formik={formik as any}
                  name="brand"
                  label="Brand"
                  placeholder="Select brand"
                  options={(Array.isArray(brands) ? brands : []).map((b) => ({ value: (b as any).name, label: (b as any).name }))}
                />
              </div>

              <div className="mb-3 col-lg-8">
                <UIReactSelectFF
                  formik={formik as any}
                  name="category"
                  label="Category"
                  placeholder="Select category"
                  options={(Array.isArray(categories) ? categories : []).map((c) => ({ value: (c as any).name, label: (c as any).name }))}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Product Description</label>
                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  rows={4}
                  value={(formik.values as any).description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{ borderRadius: 10, background: "#f5f6f8", borderColor: "#e5e7eb", fontSize: 14 }}
                  placeholder="Enter product description..."
                />
              </div>

              <div className="mb-3">
                <div className="row g-2">
                  <div className="col-6">
                    <UIInputFF
                      formik={formik as any}
                      name="threshold_qty"
                      label="Threshold Quantity"
                      type="number"
                      placeholder="0"
                    />
                  </div>
                  <div className="col-6">
                    <UIReactSelectFF
                      formik={formik as any}
                      name="statuss"
                      label="Status"
                      options={[
                        { value: "Active", label: "Active" },
                        { value: "Inactive", label: "Inactive" },
                      ]}
                      placeholder="Select"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="col-lg-6">
              <div className="font-medium header-color-product  tracking-wide uppercase mb-4">
                <i className="bi bi-currency-dollar pe-1" />
                SKU & Pricing
              </div>

              <div className="mb-3">
                <UIInputFF formik={formik as any} name="sku" label="SKU" required placeholder="Enter SKU" />
              </div>

              <div className="mb-3">
                <UIInputFF
                  formik={formik as any}
                  name="purchase_price"
                  label="Purchase Price"
                  placeholder="0.00"
                  type="number"
                />
              </div>

              <div className="mb-4">
                <UIInputFF
                  formik={formik as any}
                  name="retail_price"
                  label="Retail Price"
                  required
                  placeholder="0.00"
                  type="number"
                />
              </div>

              <div className="font-medium header-color-product  tracking-wide uppercase mb-4">
                <i className="bi bi-paperclip pe-1" />
                Physical Properties
              </div>
              <div className="border rounded-3 p-3 mb-3">
                <div className="mb-2 small text-muted">Dimensions/Measurements (Optional)</div>
                <div className="row g-2">
                  <div className="col-4">
                    <UIInputFF formik={formik as any} name="length" type="number" placeholder="Length" />
                  </div>
                  <div className="col-4">
                    <UIInputFF formik={formik as any} name="height" type="number" placeholder="Height" />
                  </div>
                  <div className="col-4">
                    <UIInputFF formik={formik as any} name="width" type="number" placeholder="Width" />
                  </div>
                  <div className="col-6">
                    <UIInputFF formik={formik as any} name="weight" type="number" placeholder="Weight (gms)" />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <UIReactSelectFF
                  formik={formik as any}
                  name="item_condition"
                  label="Item Condition"
                  placeholder="Brand New"
                  options={[
                    { value: "new", label: "New" },
                    { value: "used", label: "Used" },
                    { value: "damaged", label: "Damaged" },
                  ]}
                />
              </div>

              <div className="mb-4">
                <label className="form-label">Condition Notes</label>
                <textarea
                  id="condition_note"
                  name="condition_note"
                  className="form-control"
                  rows={4}
                  value={(formik.values as any).condition_note}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{ borderRadius: 10, background: "#f5f6f8", borderColor: "#e5e7eb", fontSize: 14 }}
                  placeholder="Enter Item Condition (optional)"
                />
              </div>
            </div>
          </div>

          {/* ====== WAREHOUSE QUANTITIES — Redesigned to match Add page (UI only) ====== */}
          <section className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Warehouse Quantities</h6>
              <small className="text-muted">
                Scroll to see more • {warehouseDetails.length} warehouses
              </small>
            </div>

            {/* Desktop style: one compact card per row */}
            <div className="rdt-desktop">
              <div
                className="production-scroll"
                style={{ maxHeight: 360, overflowY: "auto", paddingRight: 6 } as React.CSSProperties}
              >
                {paginatedData.map((item) => (
                  <div
                    key={item.warehouseId}
                    className="rounded-4 border p-3 mt-2 bg-white shadow-sm"
                  >
                    <div className="row g-3 align-items-center">
                      <div className="col-xl-6 col-lg-6">
                        <input className="form-control" readOnly value={item.label} />
                      </div>
                      <div className="col-xl-6 col-lg-6">
                        <div
                          className="d-flex align-items-center justify-content-center gap-2 mx-auto"
                          style={{
                            border: "2px solid #f2f2f2",
                            borderRadius: 25,
                            width: 140,
                            height: 40,
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() =>
                              handleWarehouseFieldChange(
                                item.warehouseId,
                                Math.max(0, (item.availableQty || 0) - 1)
                              )
                            }
                            disabled={(item.availableQty || 0) <= 0}
                          >
                            <i className="ri-subtract-line text-dark" />
                          </button>
                          <input
                            type="number"
                            min={0}
                            className="form-control text-center border-0"
                            style={{ width: 60 }}
                            value={item.availableQty}
                            onChange={(e) =>
                              handleWarehouseFieldChange(
                                item.warehouseId,
                                Number(e.target.value)
                              )
                            }
                          />
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() =>
                              handleWarehouseFieldChange(
                                item.warehouseId,
                                (item.availableQty || 0) + 1
                              )
                            }
                          >
                            <i className="ri-add-line text-dark" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Pagination controls (logic preserved) */}
                {totalPagesWQ > 1 && (
                  <div className="d-flex justify-content-center mt-3 gap-2">
                    {Array.from({ length: totalPagesWQ }).map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`btn btn-sm ${currentPage === i + 1 ? "btn-primary" : "btn-outline-secondary"
                          }`}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile style */}
            <div className="rdt-mobile">
              <div className="row g-3" style={{ maxHeight: 360, overflowY: "auto" }}>
                {paginatedData.map((item) => (
                  <div className="col-12" key={item.warehouseId}>
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <div className="fw-semibold mb-2">{item.label}</div>
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="text-muted">Available</span>
                          <div
                            className="d-flex align-items-center gap-2"
                            style={{ border: "2px solid #f2f2f2", borderRadius: 25, padding: "2px 8px" }}
                          >
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              onClick={() =>
                                handleWarehouseFieldChange(
                                  item.warehouseId,
                                  Math.max(0, (item.availableQty || 0) - 1)
                                )
                              }
                              disabled={(item.availableQty || 0) <= 0}
                            >
                              <i className="ri-subtract-line text-dark" />
                            </button>
                            <input
                              type="number"
                              min={0}
                              className="form-control text-center border-0"
                              style={{ width: 60 }}
                              value={item.availableQty}
                              onChange={(e) =>
                                handleWarehouseFieldChange(
                                  item.warehouseId,
                                  Number(e.target.value)
                                )
                              }
                            />
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              onClick={() =>
                                handleWarehouseFieldChange(
                                  item.warehouseId,
                                  (item.availableQty || 0) + 1
                                )
                              }
                            >
                              <i className="ri-add-line text-dark" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <div className="d-flex justify-content-between mt-4">
            <button className="btn back-btn surface-btn fw-bold fs-16" type="button" onClick={() => navigate(-1)}>
              <i className="bi bi-chevron-left me-2" />
              Back
            </button>
            <button className="btn btn-primary save-btn surface-btn fw-bold fs-16" type="submit" disabled={formSubmitting}>
              {formSubmitting ? "Saving…" : "Save Product"}
            </button>
          </div>
        </form>
      )}

      {/* ================= IMAGES (UI refreshed only) ================= */}
      {activeTab === "images" && (
        <div className="box-shadow-datatables shadow-border-1 p-4">
          <div className="mb-3">
            <div
              className="imageuploadui d-flex flex-column align-items-center justify-content-center"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="ri-upload-2-line fs-2 mb-2" />
              <div>Click to upload product images</div>
              <small className="text-muted">PNG, JPG up to 5MB each</small>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              multiple
              className="d-none"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                const newPreviews = files.map((file) => ({ file, url: URL.createObjectURL(file) }));
                setPreviewFiles((prev) => [...prev, ...newPreviews]);
              }}
            />
          </div>

          {/* Existing images */}
          <div className="row mt-3">
            {(uploadedFileMeta ?? []).length === 0 && previewFiles.length === 0 && (
              <div className="col-12 text-center text-muted">
                <i className="bi bi-image fs-2 d-block mb-2" />
                No images yet
              </div>
            )}

            {(uploadedFileMeta ?? []).map((file: any, index: number) => {
              const raw = file?.url;
              const src =
                getImageSrc(raw, imageUrlMap) ||
                (typeof raw === "string" && raw.startsWith("http")
                  ? raw
                  : `${String(raw || "").replace(
                    /^https?:\/\/[^/]+\/(uploads\/.*)/,
                    "$1"
                  )}`);

                  console.log(src,"trtrt");

              return (
                <div key={`saved-${index}`} className="col-6 col-md-4 col-lg-3 mb-3">
                  <div className="img-card">
                    <button
                      type="button"
                      className="btn-close img-x"
                      aria-label="Remove"
                      onClick={() => {
                        removeUploadedImage(String(file?.url || ""));
                      }}
                    />
                    {src ? (
                      <img src={src} alt={file?.alt_text || "Image"} className="img-thumb" />
                    ) : (
                      <div className="text-muted small">Loading…</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {!!previewFiles.length && (
            <>
              <div className="mt-4 small text-muted">New images (to be uploaded on Save)</div>
              <div className="row g-3 mt-1">
                {previewFiles.map(({ file, url }, idx) => (
                  <div className="col-sm-6 col-md-4 col-lg-3" key={`${file.name}-${idx}`}>
                    <div className="img-card">
                      <button
                        type="button"
                        className="btn-close img-x"
                        aria-label="Remove"
                        onClick={() => {
                          URL.revokeObjectURL(url);
                          setPreviewFiles((prev) => prev.filter((_, i) => i !== idx));
                          removePreviewImage(idx);
                        }}
                      />
                      <img src={url} alt={file.name} className="img-thumb" />
                    </div>
                    <div className="small text-center mt-1 text-muted text-truncate">
                      {file.name}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="d-flex justify-content-end mt-4">
            <button
              className="btn btn-primary save-btn surface-btn fw-bold fs-16"
              onClick={() => formik.handleSubmit()}
              disabled={formSubmitting}
            >
              {formSubmitting ? "Saving…" : "Save Product"}
            </button>
          </div>
        </div>
      )}

      {/* ================= SERIALIZATION (UNCHANGED UI for now) ================= */}
      {activeTab === "serialization" && (
        <div className="box-shadow-datatables shadow-border-1 p-4">
          <div className="row mt-2 p-4">
            <div className="col-lg-12" id="row-overlap">
              <h4 className="mb-3">Serialization – Assets Product</h4>
              {/* Warehouse Dropdown */}
              <div className="mb-3 col-lg-3 br-8">
                <label className="form-label fs-13">Select Warehouse</label>
                <Select
                  inputId="select_warehouse_id"
                  name="select_warehouse_id"
                  classNamePrefix="react-select rs"
                  placeholder="Select Warehouse"
                  // reuse your existing options map
                  options={warehouseOptions}
                  // keep the current formik value in sync with react-select
                  value={
                    warehouseOptions?.find(
                      (o: any) => o.value === (formik.values as any).select_warehouse_id
                    ) || null
                  }
                  onChange={(opt: any) => {
                    const value = opt?.value ?? "";
                    formik.setFieldValue("select_warehouse_id", value);
                    setSelectedWarehouseIdForSerialization(value);
                    fetchWarehouseSerializationData(value);
                    // Reset to first page when warehouse changes
                    setSerializedUnitsPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                />
              </div>

              {/* STATS CARDS (UI refresh only) */}
              <div className="row g-3 mb-4 text-center">
                <div className="col-lg-4">
                  <div className="serial-card shadow-lg">
                    <div className="fw-medium label mb-2">Total Quantity</div>
                    <div className="value" style={{ color: "#101828" }}>{warehouseTotals.total}</div>
                    <div className="text-muted small mt-1">in selected warehouse</div>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="serial-card shadow-lg">
                    <div className="fw-medium label mb-2">Serialized Quantity</div>
                    <div className="value" style={{ color: "#155dfc" }}>{warehouseTotals.serialized}</div>
                    <div className="text-muted small mt-1">already serialized</div>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="serial-card shadow-lg">
                    <div className="fw-medium label mb-2">Available For Bin Allocations</div>
                    <div className="value" style={{ color: "#00a63e" }}>{warehouseTotals.available}</div>
                    <div className="text-muted small mt-1">ready for bin allocation</div>
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-lg-3 mb-3 mb-lg-0">
                  <label className="form-label fs-13">Quantity to Serialize</label>
                  <input
                    type="number"
                    className="form-control form-control-lg br-8"
                    id="serialize_quantity"
                    name="serialize_quantity"
                    placeholder="Enter quantity"
                    value={serializeQuantity}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSerializeQuantity(value);
                      const num = Number(value);
                      const availableToSerialize = warehouseTotals.total - warehouseTotals.serialized;
                      if (!value || isNaN(num)) setQuantityError("Please enter a valid number.");
                      else if (num <= 0) setQuantityError("Quantity must be greater than 0.");
                      else if (num > availableToSerialize)
                        setQuantityError(`Cannot exceed available quantity (${availableToSerialize}).`);
                      else setQuantityError("");
                    }}
                  />
                  {quantityError && <div className="text-danger mt-1">{quantityError}</div>}
                </div>
                <div className="align-items-center col-lg-3 d-flex gap-3 mb-3 mb-lg-0">
                  <div className="col-lg-10">
                    <label className="form-label fs-13">Serial Prefix</label>
                    <input
                      type="text"
                      className="form-control form-control-lg br-8"
                      id="serial_prefix"
                      name="serial_prefix"
                      placeholder="e.g., POS"
                      value={formik.values.serial_prefix}
                      onChange={formik.handleChange}
                    />
                  </div>

                  <div className="col-lg-10 mt-4">
                    <button
                      className="btn btn-primary table-addbutton w-100 br-8"
                      onClick={() => handleGenerateSerialization(Number(serializeQuantity))}
                      // Enable only when: Serial Prefix has value AND no errors AND available to serialize > 0
                      disabled={
                        !!quantityError ||
                        !serializeQuantity ||
                        !(formik.values.serial_prefix && formik.values.serial_prefix.trim()) ||
                        (warehouseTotals.total - warehouseTotals.serialized) <= 0
                      }
                    >
                      Generate Serialization
                    </button>
                  </div>
                </div>
              </div>

              {/* Search Records and Filter Section */}
              <div className="justify-content-between mb-4 mt-4 row">
                <div className="col-lg-4 mb-3 mb-lg-0">
                  <label className="form-label fs-13">Search Records</label>
                  <div className="position-relative">
                    <i className="ri-search-line position-absolute fs-17" style={{ left: "12px", top: "50%", transform: "translateY(-50%)", color: "#99a1af", zIndex: 10 }}></i>
                    <input
                      type="text"
                      className="form-control form-control-lg br-8 ps-5"
                      placeholder="Search by serial, order, warehouse..."
                      value={searchInput}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === "Enter") {
                          setSerializedUnitsPagination((prev) => ({ ...prev, pageIndex: 0 }));
                          setSerializedUnitsGlobalFilter(searchInput);
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="col-lg-3 mb-3 mb-lg-0">
                  <label className="form-label fs-13">Filter by Status</label>
                  <Select
                    inputId="status_filter"
                    name="status_filter"
                    classNamePrefix="react-select rs"
                    placeholder="All Statuses"
                    options={[
                      { value: "all", label: "All Statuses" },
                      { value: "Damaged", label: "Damaged" },
                      { value: "Lost", label: "Lost" },
                      { value: "Under maintenance", label: "Repair Required" },
                    ]}
                    value={statusFilter}
                    onChange={(opt: any) => {
                      setStatusFilter(opt);
                      setSerializedUnitsPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    components={{ DropdownIndicator, Control: ControlWithFilter }}
                    styles={customSelectStyles}
                  />
                </div>
              </div>

              {/* <div className="row mb-3 justify-content-end">
                <div className="col-lg-3 d-flex align-items-end mb-3 mb-lg-0">
                  <button
                    className="btn back-btn surface-btn fw-medium"
                    onClick={() => {
                      // TODO: Implement generate all missing barcodes functionality
                      showSuccess("Generate All Missing Barcodes functionality will be implemented");
                    }}
                  >
                    <i className="ri-barcode-line me-2"></i>
                    Generate All Missing Barcodes
                  </button>
                </div>
              </div> */}

              {/* Datatable */}
              <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables mt-4">
                <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
                  <h5 className="mb-0"></h5>
                </div>
                <hr className="mb-2" style={{ borderTop: "none" }} />
                
                {serializedUnitsLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* DESKTOP (≥ 1022px) */}
                    <div className="rdt-desktop shadow-sm">
                      <div
                        className="production-scroll"
                        style={{ "--rdt-min": "1200px" } as React.CSSProperties}
                      >
                        <table className="table production-table align-middle mb-0 table-1">
                          <DesktopTableParts table={serializedUnitsTable} />
                        </table>
                      </div>
                    </div>

                    {/* MOBILE (≤ 1021px) */}
                    <div className="rdt-mobile">
                      <MobileCardsOnly
                        table={serializedUnitsTable}
                        idColId="serial"
                        titleColId="serial_number"
                        actionColId="actions"
                      />
                    </div>
                  </>
                )}

                {serializedUnitsTable.getRowModel().rows.length === 0 && !serializedUnitsLoading ? (
                  <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
                    No records found
                  </div>
                ) : serializedUnitsTable.getRowModel().rows.length > 0 ? (
                  <div className="mt-4 mb-4">
                    <TablePaginationControls table={serializedUnitsTable} />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= ALLOCATION (UNCHANGED UI for now) ================= */}
      {activeTab === "allocation" && (
        <div className="box-shadow-datatables shadow-border-1 p-4">
          {rotationalProducts && (
            <AllocationToBins
              warehouses={warehouses}
              rotationalProduct={rotationalProducts}
              userId={userId || ""}
              token={token || ""}
              productId={productId || ""}
            />
          )}
        </div>
      )}

      {/* Scoped styles */}
      <style>{`
        .rc-tabs{display:flex;gap:8px;flex-wrap:wrap}
        .rc-tab{border:1px solid #e5e7eb;background:#f8f9fb;padding:8px 14px;border-radius:10px;font-weight:600;color:#374151}
        .rc-tab:hover{background:#eef2ff}
        .rc-tab.rc-tab-active{background:#6d28d9;border-color:#6d28d9;color:#fff;box-shadow:0 4px 14px rgba(109,40,217,.18)}
        
        /* Specific tab colors for rotational product */
        .rc-tab:nth-child(1).rc-tab-active{background:#2563eb !important;border-color:#2563eb !important;box-shadow:0 4px 14px rgba(37,99,235,.18) !important}
        .rc-tab:nth-child(2).rc-tab-active{background:#8b5cf6 !important;border-color:#8b5cf6 !important;box-shadow:0 4px 14px rgba(139,92,246,.18) !important}
        .rc-tab:nth-child(3).rc-tab-active{background:#06b6d4 !important;border-color:#06b6d4 !important;box-shadow:0 4px 14px rgba(6,182,212,.18) !important}
        .rc-tab:nth-child(4).rc-tab-active{background:#f59e0b !important;border-color:#f59e0b !important;box-shadow:0 4px 14px rgba(245,158,11,.18) !important}
        
        .imageuploadui{border:2px dashed #d1d5db;border-radius:14px;padding:32px 12px;text-align:center;cursor:pointer;transition:all .2s}
        .imageuploadui:hover{border-color:#6d28d9;background:#fbfaff}
        .uploadedimages{border-radius:10px}
          /* Thumbnail card so all X icons share the same anchor */
.img-card{
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  height: 180px;                 /* same height for all tiles */
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Image fits nicely without changing card height */
.img-thumb{
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 10px;
}

/* Close button perfectly aligned inside the tile */
.img-x{
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 2;
  opacity: 0.9;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,.15);
  padding: 0;
}

/* optional: hover feedback */
.img-card:hover .img-x{ opacity: 1; }

        /* Responsive toggles for redesigned warehouse cards */
        .rdt-desktop { display: none; }
        .rdt-mobile { display: block; }
        @media (min-width: 768px) {
          .rdt-desktop { display: block; }
          .rdt-mobile { display: none; }
        }
          .serial-card{
            background:#fff;
            border:1px solid #e5e7eb;
            border-radius:12px;
            padding:18px;
          }
          .serial-card .label{ color:#6b7280; font-size:13px; }
          .serial-card .value{ font-weight:600; font-size:21px; line-height:1; }
          @media (max-width: 767.98px){
            .serial-card{ padding:14px; }
            .serial-card .value{ font-size:24px; }
          }

          /* Custom dropdown arrow for select fields */
          .select-with-arrow {
            appearance: none;
            -webkit-appearance: none;
            -moz-appearance: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 0.75rem center;
            background-size: 12px 12px;
            padding-right: 2.5rem;
          }

      `}</style>
    </div>
  );
};

export default SignageEditRotationalProduct;
